import { css } from '@emotion/react';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { isEmpty } from 'underscore';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { Button } from 'react-bootstrap';

import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { fetchSurveyQuestions } from 'redux/actions/analytics';
import { QuizQuestion, QuizQuestionType } from 'redux/schemas/models/quiz';
import { QuestionMapping } from 'redux/schemas/app/analytics';

import { gray4 } from 'styles/global_defaults/colors';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';

import { extractTextFromHTML } from 'shared/utils';
import usePrevious from 'shared/hooks/use-previous';

import NvDropdown, { NvDropdownTextItem, NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import LoadingWrapper, { LoaderType } from 'shared/components/loading-wrapper';
import { AnalyticsContext } from '../analytics-dashboard';
import { config } from '@config/pendo.config.json';
import NpsCsatSelection from './nps-csat-selection';

const styles = css`
  .bs4-dropdown {
    width: 340px;
    border: 1px solid ${gray4};
  }
  .bs4-dropdown-item {
    white-space: normal !important;
  }
  .bs4-dropdown .title {
    height: auto !important;
  }
  .dropdown-target {
    width: 340px;
    border-radius: ${quarterSpacing}px;
  }
  .pre-question {
    width: 340px;
    flex-shrink: 0;
  }
  .question {
    white-space: normal;
    text-align: left;
    min-width: 305px; // 340-20-10-5 (container padding, icon width, margin)
  }
`;
interface QuestionsMappingProps {
  preCatalogId?: string;
}

const QuestionsMapping: React.FC<QuestionsMappingProps> = ({
  preCatalogId,
}) => {
  const dispatch = useAppDispatch();
  const {
    watch,
    setValue,
    formState: { isValid, isSubmitting, isDirty },
  } = useFormContext();
  const { prePostSurveyConfigurationId: configurationId } = useContext(AnalyticsContext);

  // Form values
  const preSurveyId = watch('preCourseSurveyId');
  const postSurveyId = watch('postCourseSurveyId');
  const postCatalogId = watch('postCourseCatalogId');
  const questionMappings: QuestionMapping[] = watch('questionMappings');

  const previousPostSurveyId = usePrevious(postSurveyId);
  const previousPreSurveyId = usePrevious(preSurveyId);

  // Redux state
  const preSurveyQuestions: QuizQuestion[] = useSelector(
    state => state.app.analytics.surveyQuestions?.[preSurveyId],
  );
  const postSurveyQuestions: QuizQuestion[] = useSelector(
    state => state.app.analytics.surveyQuestions?.[postSurveyId],
  );
  const preSurveyQuestionsLoaded = useSelector(state => state.app.analytics.loadedSurveyQuestions?.[preSurveyId]);
  const postSurveyQuestionsLoaded = useSelector(state => state.app.analytics.loadedSurveyQuestions?.[postSurveyId]);

  // Convert mappings array to lookup map for O(1) access
  const mappingLookup = useMemo(() => {
    const map: Record<number, number> = {};
    questionMappings?.forEach(({ preQuestionId, postQuestionId }) => {
      map[preQuestionId] = postQuestionId;
    });
    return map;
  }, [questionMappings]);

  // Auto-generate mappings for questions with matching text.
  // Each post-question can only be mapped once (first match wins).
  const autoGenerateMappings = useMemo(() => {
    if (isEmpty(preSurveyQuestions) || isEmpty(postSurveyQuestions)) return [];

    const usedPostQuestionIds = new Set<number>();

    return preSurveyQuestions.reduce<QuestionMapping[]>((mappings, preQ) => {
      const match = postSurveyQuestions.find(
        postQ => (
          postQ.type === preQ.type
            && extractTextFromHTML(postQ.questionText).trim() === extractTextFromHTML(preQ.questionText).trim()
            && !usedPostQuestionIds.has(postQ.id)
        ),
      );

      if (match) {
        usedPostQuestionIds.add(match.id);
        mappings.push({ preQuestionId: preQ.id, postQuestionId: match.id });
      }

      return mappings;
    }, []);
  }, [preSurveyQuestions, postSurveyQuestions]);

  const setMappingsToForm = useCallback((mappings: QuestionMapping[]) => {
    setValue(
      'questionMappings',
      mappings,
      { shouldDirty: true, shouldValidate: true },
    );
  }, [setValue]);

  // Update a single mapping
  const updateMapping = useCallback((preQuestionId: number, postQuestionId: number) => {
    const existingIndex = questionMappings.findIndex(m => m.preQuestionId === preQuestionId);

    const updated = existingIndex >= 0
      ? questionMappings.map((m, idx) => (idx === existingIndex ? { preQuestionId, postQuestionId } : m))
      : [...questionMappings, { preQuestionId, postQuestionId }];

    setMappingsToForm(updated);
  }, [questionMappings, setMappingsToForm]);

  // Get available post questions for a pre question (filtered by type and already mapped)
  const getAvailablePostQuestions = useCallback((preQuestionId: number, questionType: QuizQuestionType) => {
    const alreadyMappedIds = questionMappings
      ?.filter(m => m.preQuestionId !== preQuestionId)
      ?.map(m => m.postQuestionId);

    return postSurveyQuestions?.filter(
      postQ => postQ.type === questionType && !alreadyMappedIds.includes(postQ.id),
    );
  }, [questionMappings, postSurveyQuestions]);

  // Build dropdown items
  const buildDropdownItems = useCallback((preQuestionId: number, questionType: QuizQuestionType): NvDropdownOption[] => {
    const available = getAvailablePostQuestions(preQuestionId, questionType);

    return (available?.flatMap((postQ, idx, arr) => {
      const items: NvDropdownOption[] = [{
        text: extractTextFromHTML(postQ.questionText),
        type: 'text',
        textClassName: 'text-regular',
        callback: () => updateMapping(preQuestionId, postQ.id),
      }];

      if (idx < arr.length - 1) {
        items.push({ type: 'divider' });
      }

      return items;
    }) as NvDropdownTextItem[]) ?? [];
  }, [getAvailablePostQuestions, updateMapping]);

  // Get display title for mapped question
  const getMappedQuestionTitle = (preQuestionId: number) => {
    const postQuestionId = mappingLookup[preQuestionId];
    const postQuestion = postSurveyQuestions?.find(q => q.id === postQuestionId);

    if (!postQuestion) {
      return t.COURSE_ANALYTICS.PRE_POST.MODAL.SELECT_PLACEHOLDER();
    }

    return extractTextFromHTML(postQuestion.questionText);
  };

  const getMappedQuestionTitleClass = (preQuestionId: number, hasOptions: boolean): string => {
    if (mappingLookup[preQuestionId]) {
      return 'text-regular mr-1 question';
    }

    return hasOptions ? 'text-gray-2' : 'text-gray-4';
  };

  const getSubmitButtonLabel = useCallback(() => {
    if (configurationId) {
      return isSubmitting ? t.FORM.UPDATING() : t.FORM.UPDATE();
    }
    return isSubmitting ? t.COURSE_ANALYTICS.PRE_POST.MODAL.ANALYZING() : t.COURSE_ANALYTICS.PRE_POST.MODAL.ANALYZE();
  }, [configurationId, isSubmitting]);

  // Auto-apply mappings when surveys change or questions load
  useEffect(() => {
    const preSurveyChanged = previousPreSurveyId && previousPreSurveyId !== preSurveyId;
    const postSurveyChanged = previousPostSurveyId && previousPostSurveyId !== postSurveyId;
    const shouldApplyAutoMappings = questionMappings?.length === 0 && autoGenerateMappings?.length > 0;

    // Apply auto-mappings when:
    // 1. Survey changed (clear if survey is null, otherwise apply auto-mappings)
    // 2. Auto-mappings became available and current mappings are empty
    if (preSurveyChanged || postSurveyChanged) {
      const mappings = (!preSurveyId || !postSurveyId) ? [] : autoGenerateMappings;
      setMappingsToForm(mappings);
    } else if (shouldApplyAutoMappings) {
      setMappingsToForm(autoGenerateMappings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    preSurveyId,
    postSurveyId,
    previousPreSurveyId,
    previousPostSurveyId,
    autoGenerateMappings,
    setMappingsToForm,
  ]);

  // Fetch questions
  useEffect(() => {
    if (preSurveyId && !preSurveyQuestionsLoaded) {
      dispatch(fetchSurveyQuestions({ catalogId: preCatalogId, questionSetId: preSurveyId }));
    }
  }, [dispatch, preCatalogId, preSurveyId, preSurveyQuestionsLoaded]);

  useEffect(() => {
    if (postSurveyId && !postSurveyQuestionsLoaded) {
      dispatch(fetchSurveyQuestions({ catalogId: postCatalogId, questionSetId: postSurveyId }));
    }
  }, [dispatch, postCatalogId, postSurveyId, postSurveyQuestionsLoaded]);

  // Early return if data not ready
  if (!preSurveyId || !postSurveyId) {
    return null;
  }

  return (
    <div css={styles}>
      <LoadingWrapper
        isLoaded={preSurveyQuestionsLoaded && postSurveyQuestionsLoaded}
        loaderType={LoaderType.PLACEHOLDER}
        loadingText={t.COURSE_ANALYTICS.PRE_POST.MODAL.QUESTIONS_LOADING()}
        keepRendered={false}
      >
        <div className='mb-4 font-weight-bolder'>
          {t.COURSE_ANALYTICS.PRE_POST.MODAL.MAP_QUESTIONS()}
        </div>
        <div className='mb-4'>
          {t.COURSE_ANALYTICS.PRE_POST.MODAL.MAPPING_INFO()}
        </div>
        <div className='d-flex mb-2'>
          <div className='pre-question'>
            {t.COURSE_ANALYTICS.PRE_POST.MODAL.MAPPING_HEADER_1()}
          </div>
          <div className='pl-5'>
            {t.COURSE_ANALYTICS.PRE_POST.MODAL.MAPPING_HEADER_2()}
          </div>
        </div>

        {preSurveyQuestions?.map((preQuestion) => {
          const dropdownItems = buildDropdownItems(preQuestion.id, preQuestion.type);
          const hasOptions = dropdownItems?.length > 0;
          return (
            <div key={preQuestion.id} className='d-flex align-items-start mb-2 text-regular'>
              <div className='pre-question'>
                <div className='d-flex bg-gray-7 rounded p-2'>
                  {extractTextFromHTML(preQuestion.questionText)}
                </div>
              </div>

              <div className='pl-5'>
                <NvDropdown
                  items={dropdownItems}
                  buttonStyle={NvDropdownButtonStyle.FORM}
                  showSelectedIndicator
                  maxWidth={340}
                  minWidth={340}
                  title={getMappedQuestionTitle(preQuestion.id)}
                  titleClass={getMappedQuestionTitleClass(preQuestion.id, hasOptions)}
                  disabled={!hasOptions}
                  tooltip={!hasOptions ? t.COURSE_ANALYTICS.PRE_POST.MODAL.DISABLED_TOOLTIP() : undefined}
                />
              </div>
            </div>
          );
        })}

        {preCatalogId && <NpsCsatSelection preCatalogId={preCatalogId} />}

        <div className='d-flex justify-content-center mt-5'>
          <Button
            variant='primary'
            type='submit'
            disabled={!isValid || isSubmitting || !isDirty}
            data-qa={config.pendo.analyticsDashboard.prePost.setupModal.analyze}
          >
            {getSubmitButtonLabel()}
          </Button>
        </div>
      </LoadingWrapper>
    </div>
  );
};

export default QuestionsMapping;
